"use client"

import { ChevronDown, Info } from "lucide-react"

import { accountText, type LocaleProps } from "../../personal-web"
import { parseLpa } from "../lpa"

/**
 * Public-guide placeholder for ManualCard. Same shape as ManualCard but
 * shows an example LPA (clearly labeled as "Looks like…") instead of a
 * real one. No Copy buttons — there's nothing to copy.
 *
 * The example string uses `example.com` so it's obviously not a working
 * carrier endpoint, and a 32-char hex Activation Code so the format
 * matches what users will see in their real eSIM.
 *
 * Same `<details>` split affordance as ManualCard — readers learn that
 * SM-DP+ and Activation Code come from inside the LPA string, by clicking
 * exactly where they will on their real install page.
 */
export function ManualCardPlaceholder({ lng }: LocaleProps) {
  // Example LPA from accountText so translations can localise the SMDP+
  // suffix if needed. Defaults to a recognisable fake.
  const exampleLpa = accountText(lng, "install.guide.manualPlaceholderExampleLpa")
  const parsed = parseLpa(exampleLpa)

  return (
    <section className="rounded-2xl border border-stone-200 bg-white p-5 md:p-6">
      <FieldExampleRow
        lng={lng}
        labelKey="install.fieldLpa"
        value={exampleLpa}
      />

      {parsed ? (
        <details className="group mt-4 overflow-hidden rounded-xl border border-stone-200">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-stone-50 [&::-webkit-details-marker]:hidden">
            <span>{accountText(lng, "install.manualCard.splitToggle")}</span>
            <ChevronDown
              className="h-4 w-4 shrink-0 text-stone-500 transition-transform group-open:rotate-180"
              aria-hidden
            />
          </summary>
          <div className="border-t border-stone-100 p-4">
            <div className="grid gap-4">
              <FieldExampleRow
                lng={lng}
                labelKey="install.fieldSmdp"
                value={parsed.smdp}
              />
              <FieldExampleRow
                lng={lng}
                labelKey="install.fieldActivationCode"
                value={parsed.activationCode}
              />
            </div>
          </div>
        </details>
      ) : null}

      <div className="mt-4 flex items-start gap-2 rounded-xl bg-sky-50 p-3 text-xs leading-relaxed text-sky-900">
        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>{accountText(lng, "install.guide.manualPlaceholderNote")}</span>
      </div>
    </section>
  )
}

function FieldExampleRow({
  lng,
  labelKey,
  value,
}: LocaleProps & {
  labelKey: string
  value: string
}) {
  return (
    <div className="grid gap-1.5">
      <dt className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-stone-500">
        {accountText(lng, labelKey)}
        <span className="rounded-full bg-stone-100 px-1.5 py-0.5 text-[9px] font-semibold normal-case tracking-normal text-stone-500">
          {accountText(lng, "install.guide.exampleBadge")}
        </span>
      </dt>
      <dd>
        <code className="block w-full truncate rounded-lg bg-stone-100 px-3 py-2.5 font-sans text-[13px] text-stone-500">
          {value}
        </code>
      </dd>
    </div>
  )
}
